import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchPostsAndCategories = async () => {
      try {
        const [postsRes, categoriesRes] = await Promise.all([
          fetch('/api/blog_posts'),
          fetch('/api/categories')
        ]);

        if (!postsRes.ok || !categoriesRes.ok) {
          throw new Error(`HTTP error!`);
        }

        const postsData = await postsRes.json();
        const categoriesData = await categoriesRes.json();

        setPosts(postsData);
        setCategories(categoriesData);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchPostsAndCategories();
  }, []);

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (post.content && post.content.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === null || (post.category_id !== null && Number(post.category_id) === Number(selectedCategory));
    return matchesSearch && matchesCategory;
  });

  const popularPosts = posts.slice(0, 3); // Take first 3 as popular for now

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "İbrahim Levent - Blog",
    "description": "Full Stack Web Development, teknoloji ve kariyer üzerine yazılar.",
    "url": "https://ibrahimlevent.com.tr/blog",
    "author": {
        "@type": "Person",
        "name": "İbrahim Levent"
    },
    "publisher": {
        "@type": "Organization",
        "name": "İbrahim Levent",
        "logo": {
            "@type": "ImageObject",
            "url": "https://ibrahimlevent.com.tr/logo512.png"
        }
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Yükleniyor...</div>;
  }

  if (error) {
    return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Hata: {error.message}</div>;
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-900 text-white py-12 lg:py-20">
        <Helmet>
          <title>Blog - İbrahim Levent</title>
          <meta name="description" content="Full Stack Web Development, teknoloji ve kariyer üzerine yazılarım." />
          <link rel="canonical" href="https://ibrahimlevent.com.tr/blog" />
          <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
        </Helmet>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content Area - Blog Posts */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filteredPosts.length > 0 ? (
                  filteredPosts.map((post) => (
                    <Link to={`/blog/${post.slug}`} key={post.id} className="block group">
                      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 shadow-lg overflow-hidden h-full flex flex-col transition-transform duration-300 ease-in-out transform hover:-translate-y-2 hover:shadow-2xl">
                        {post.image_url && (
                          <div className="aspect-w-16 aspect-h-9 w-full overflow-hidden">
                            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300" />
                          </div>
                        )}
                        <div className="p-6 flex flex-col flex-grow">
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">{post.title}</h3>
                          <p className="text-gray-400 text-sm mb-4">{new Date(post.created_at).toLocaleDateString()}</p>
                          <div className="text-gray-400 text-sm mb-4" dangerouslySetInnerHTML={{ __html: post.content.substring(0, 150) + '...' }} />
                          <div className="mt-auto pt-4 border-t border-gray-700/50">
                            <span className="text-sm text-blue-400 font-semibold">Devamını Oku &rarr;</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-400 text-center lg:col-span-2">Aradığınız kriterlere uygun yazı bulunamadı.</p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-8">
              {/* Search Bar */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-lg">
                <h3 className="text-xl font-bold text-white mb-4">Makale Ara</h3>
                <input
                  type="text"
                  placeholder="Anahtar kelime girin..."
                  className="w-full bg-gray-900 border border-gray-700 rounded-md px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Categories */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-lg">
                <h3 className="text-xl font-bold text-white mb-4">Kategoriler</h3>
                <ul>
                  {categories.map((category) => (
                    <li key={category.id} className="mb-2 last:mb-0">
                      <button onClick={() => setSelectedCategory(category.id)} className={`flex justify-between items-center w-full text-left ${selectedCategory === category.id ? 'text-blue-400' : 'text-gray-400 hover:text-blue-400'} transition-colors`}>
                        <span>{category.name}</span>
                        <span className="bg-gray-700/50 text-xs px-2 py-0.5 rounded-full">{posts.filter(post => {
  const postCategoryId = Number(post.category_id);
  const categoryIdNum = Number(category.id);
  return !isNaN(postCategoryId) && !isNaN(categoryIdNum) && postCategoryId === categoryIdNum;
}).length}</span>
                      </button>
                    </li>
                  ))}
                  <li className="mb-2 last:mb-0">
                    <button onClick={() => setSelectedCategory(null)} className={`flex justify-between items-center w-full text-left ${selectedCategory === null ? 'text-blue-400' : 'text-gray-400 hover:text-blue-400'} transition-colors`}>
                      <span>Tüm Kategoriler</span>
                      <span className="bg-gray-700/50 text-xs px-2 py-0.5 rounded-full">{posts.length}</span>
                    </button>
                  </li>
                </ul>
              </div>

              {/* Popular Articles */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-lg">
                <h3 className="text-xl font-bold text-white mb-4">Popüler Makaleler</h3>
                <ul>
                  {popularPosts.map((post) => (
                    <li key={post.id} className="mb-4 last:mb-0">
                      <Link to={`/blog/${post.slug}`} className="block group">
                        <h4 className="text-md font-semibold text-white group-hover:text-blue-400 transition-colors">{post.title}</h4>
                        <p className="text-gray-400 text-sm">{new Date(post.created_at).toLocaleDateString()}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default BlogPage;